import { gql } from "@apollo/client";
import _ from "lodash";
import {
  BookTextIcon,
  BuildingIcon,
  FileTextIcon,
  TagIcon,
  ToggleRightIcon,
} from "lucide-react";
import { useShallow } from "zustand/react/shallow";

import {
  RuleDefinitionSuggestionTypeEnum,
  RuleFragment,
} from "@/__generated__/graphql";
import {
  DraftRuleSuggestion,
  RuleConditionsActionsView,
  RuleEditor,
} from "@/app/app/_/views/RulesView/RuleEditor";
import { useRuleEditorState } from "@/app/app/_/views/RulesView/RuleDetailPage.hooks";
import { RuleExecutionsTab } from "@/app/app/_/views/RulesView/RuleExecutionsTab";
import type { FilterGroup } from "@/app/app/_/views/RulesView/types/rule";
import { serializeFilterGroup } from "@/app/app/_/views/RulesView/utils/filterGroup";
import { SUGGESTION_TYPE_LABELS } from "@/app/app/_/views/RulesView/utils/nodeSummary";
import { RuleObjectPanel } from "@/app/app/_/views/RulesView/RuleObjectPanel";
import { ruleTriggerEventClause } from "@/app/app/_/views/RulesView/RuleWhenDescription";
import { BoxForEntity } from "@/components/atoms/BoxForEntity";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import {
  FieldPaginatedTableValueOptions,
  TableColumnType,
} from "@/components/@types";
import { FieldPaginatedTable } from "@/components/organisms/Field";
import { useNodeLayoutStore } from "@/components/organisms/Node/Node.hooks";
import { Queries } from "@/constants";
import { EnumColors, Values } from "@/constants/values";
import { getReferenceValueOptionsByTypename, Objects } from "@/objects";
import { iconsByTypename } from "@/objects/@icons";
import { ObjectCardProps, ObjectType } from "@/objects/@types";
import { toColumns } from "@/objects/@utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

function isRuleEnabled(raw: string | boolean | null | undefined): boolean {
  if (typeof raw === "boolean") return raw;
  return raw === "ACTIVE";
}

const RULE_SUGGESTION_TYPE_OPTIONS = Object.values(
  RuleDefinitionSuggestionTypeEnum,
).map((value) => ({
  key: value,
  value: SUGGESTION_TYPE_LABELS[value],
}));

function primarySuggestionType(
  data: RuleFragment | null | undefined,
): RuleDefinitionSuggestionTypeEnum | null {
  const suggestion = data?.suggestions?.[0];
  if (!suggestion || !("type" in suggestion) || !suggestion.type) {
    return null;
  }
  return suggestion.type;
}

type RuleEntityRef = {
  id: string;
  identifier: string;
  name?: string | null;
};

function ruleApplicableEntities(
  data: RuleFragment | null | undefined,
): RuleEntityRef[] {
  const seen = new Set<string>();
  const entities: RuleEntityRef[] = [];
  const push = (
    entity?: {
      id?: string | null;
      identifier?: string | null;
      name?: string | null;
    } | null,
  ) => {
    if (!entity?.id || !entity.identifier || seen.has(entity.identifier)) {
      return;
    }
    seen.add(entity.identifier);
    entities.push({
      id: entity.id,
      identifier: entity.identifier,
      name: entity.name,
    });
  };

  push(data?.entity);
  for (const delegation of data?.delegations ?? []) {
    push(delegation.entity);
  }
  for (const suggestion of data?.suggestions ?? []) {
    if (
      suggestion.__typename === "RuleDefinitionSuggestionMatchDocumentObject" &&
      "settlementEntities" in suggestion
    ) {
      for (const entity of suggestion.settlementEntities ?? []) {
        push(entity);
      }
    }
  }
  return entities;
}

/** The rule being created, as the create form holds it. */
type RuleDraft = {
  name?: string;
  conditions?: FilterGroup;
  suggestions?: DraftRuleSuggestion[];
};

/**
 * The rule being created, in the same editor a saved rule opens in. It holds
 * the conditions and actions in the form, and they're sent with the rule.
 */
function RuleDraftTab() {
  const store = useNodeLayoutStore();
  const draft = store(
    useShallow((state) => {
      const values = state.fieldValues as RuleDraft;
      return {
        name: values.name,
        conditions: values.conditions,
        suggestions: values.suggestions,
      };
    }),
  );
  const rule = {
    __typename: "RuleDefinitionObject",
    id: "",
    identifier: "",
    name: draft.name ?? "",
    conditions: [],
    suggestions: draft.suggestions ?? [],
  } as unknown as RuleFragment;
  const setValues = (values: RuleDraft) =>
    store.setState({
      fieldValues: { ...store.getState().fieldValues, ...values },
    });

  return (
    <div className="p-5 w-full" data-testid="rule-draft-editor">
      <RuleConditionsActionsView
        rule={rule}
        initialFilters={draft.conditions}
        onPersistFilters={(conditions) => setValues({ conditions })}
        onRuleChange={(updater) => {
          const current = store.getState().fieldValues as RuleDraft;
          setValues({
            suggestions: updater({
              ...rule,
              suggestions: current.suggestions ?? [],
            } as RuleFragment).suggestions as DraftRuleSuggestion[],
          });
        }}
      />
    </div>
  );
}

/**
 * The rule said as sentences - "When … Then …" - wherever it is opened, as on
 * its own page: a saved one in the rules page's editor, one being created in
 * the form's.
 */
function RuleTab() {
  const store = useNodeLayoutStore();
  const savedRuleId = store((state) => {
    const values = state.fieldValues as { id?: string; identifier?: string };
    return values.identifier ? (values.id ?? null) : null;
  });
  return savedRuleId ? <RuleSavedTab ruleId={savedRuleId} /> : <RuleDraftTab />;
}

/** A saved rule, in the editor its page opens it in. */
function RuleSavedTab({ ruleId }: { ruleId: string }) {
  useRuleEditorState(ruleId);
  return <RuleEditor />;
}

/**
 * The transactions a rule being created would also apply to, passed in by the
 * flow that opened the form (``_similarTransactionIdentifiers``).
 */
function RuleAppliesToTab() {
  const store = useNodeLayoutStore();
  const identifiers = store(
    useShallow(
      (state) =>
        (state.fieldValues as { _similarTransactionIdentifiers?: string[] })
          ._similarTransactionIdentifiers ?? [],
    ),
  );
  const valueOptions: FieldPaginatedTableValueOptions = {
    columns: toColumns(Objects.TransactionObject).filter((column) =>
      ["self", "effectiveDate", "amount", "financialAccount"].includes(
        column.objectKey as string,
      ),
    ) as TableColumnType[],
    connection: {
      query: Queries.TRANSACTIONS_CONNECTION_QUERY,
      queryKey: "transactionsConnection",
      pageSize: 50,
      getVariables: (_row, { entityIdentifiers }) => ({
        entityIdentifiers,
        filter: { field: { identifier: { inList: identifiers } } },
      }),
    },
  };

  return (
    <div className="flex-1 min-h-0 w-full flex flex-col overflow-hidden">
      <Typography
        level="body-sm"
        className="pl-4 pr-4 pt-2.5 pb-2.5 text-muted-foreground"
      >
        {identifiers.length === 1
          ? "Creating the rule also categorizes this open transaction."
          : `Creating the rule also categorizes these ${identifiers.length} open transactions.`}
      </Typography>
      <FieldPaginatedTable
        data={{ _similarTransactionIdentifiers: identifiers }}
        valueOptions={valueOptions}
        style={{ width: "100%", height: "100%", flex: 1, minHeight: 0 }}
      />
    </div>
  );
}

/**
 * A rule as a card: what it does, in its own words, and how it runs - what
 * sets it off, and whether it applies what it suggests by itself.
 */
function RuleCard({ data, isLoading = false }: ObjectCardProps<RuleFragment>) {
  // In the words the rule's own page says it in: "when a bank transaction is
  // created", "on a monthly schedule", "runs manually".
  const clause = data ? ruleTriggerEventClause(data) : null;
  const trigger = clause
    ? _.upperFirst(clause.startsWith("a ") ? `when ${clause}` : clause)
    : null;
  return (
    <div className="flex flex-col gap-1.5 h-full min-h-0">
      {data?.description && (
        <Typography
          level="body-sm"
          className="text-muted-foreground line-clamp-3"
        >
          <Skeleton loading={isLoading} variant="inline">
            {data.description}
          </Skeleton>
        </Typography>
      )}
      <div className="mt-auto flex gap-1 flex-wrap">
        {trigger && (
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
          >
            <SimpleEnum
              label={trigger}
              enumColor={EnumColors.GREY_1}
              size="sm"
            />
          </Skeleton>
        )}
        {data?.isAutoApply && (
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
          >
            <SimpleEnum
              label="Applies itself"
              enumColor={EnumColors.BLUE}
              size="sm"
            />
          </Skeleton>
        )}
      </div>
    </div>
  );
}

const object: ObjectType<RuleFragment> = {
  humanName: "rule",
  inputKey: "rule",
  typename: "RuleDefinitionObject",
  ReactIcon: BookTextIcon,
  isEntity: false,
  titleFieldKey: "name",
  PanelOverride: RuleObjectPanel,
  panelDataKeys: [
    "name",
    "ruleStatus",
    "entity",
    "delegations",
    "suggestions",
    "conditions",
  ],
  getTitle: (data) => {
    return data?.name;
  },
  getEntityId: (data) => {
    return data?.entity?.id;
  },
  Card: RuleCard,
  placeholder: {
    __typename: "RuleDefinitionObject",
    id: btoa("RuleDefinitionObject:placeholder"),
    identifier: "placeholder",
    name: "Match receipts from Gmail",
    description:
      "Finds the receipt for a card payment in the mailbox and matches it",
    triggerType: "event",
    triggerObjectType: "TransactionObject",
    triggerOperation: "ON_CREATE",
    isAutoApply: true,
    delegations: [],
    suggestions: [],
    conditions: [],
    entity: {
      id: btoa("EntityObject:placeholder"),
      identifier: "placeholder",
      name: "Elm Street Coffee",
    },
  } as unknown as RuleFragment,
  getReferenceValueOptions: (entityIdentifiers) => {
    return {
      query: gql`
        query RuleList($entityIdentifiers: [String!]) {
          rules(entityIdentifiers: $entityIdentifiers) {
            data {
              ...Rule
            }
          }
        }
      `,
      queryOptions: {
        variables: {
          entityIdentifiers: entityIdentifiers,
        },
      },
      onRenderOption: (option: RuleFragment) => {
        return <>{option.name}</>;
      },
      mutationOptions: {
        createTypename: "RuleDefinitionObject",
      },
    };
  },
  groups: [
    {
      id: "rule",
      label: "Rule",
      component: RuleTab,
    },
    { id: "overview", label: "Overview" },
    {
      id: "appliesTo",
      label: "Also applies to",
      component: RuleAppliesToTab,
      flush: true,
      isVisible: (data) =>
        ((data as { _similarTransactionIdentifiers?: string[] })
          ?._similarTransactionIdentifiers?.length ?? 0) > 0,
    },
    {
      id: "executions",
      label: "Executions",
      component: RuleExecutionsTab,
      flush: true,
      // Executions belong to a saved rule, not one being created.
      isVisible: (data) => Boolean(data?.identifier),
    },
  ],
  fieldDefinitions: [
    // The list's first columns, as every record list's: the business the rule
    // is written in, then the rule itself - opening on its own page.
    {
      objectKey: "entity",
      inputKey: "entity",
      label: "",
      type: "custom",
      ReactIcon: BuildingIcon,
      asString: (data) => data?.entity?.identifier,
      getValue: (data) => () => {
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BoxForEntity
              identifier={data?.entity?.identifier}
              representation="character"
            />
          </div>
        );
      },
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: false,
        minWidth: Values.DEFAULT_ROW_HEIGHT,
        width: Values.DEFAULT_ROW_HEIGHT,
        maxWidth: Values.DEFAULT_ROW_HEIGHT,
      },
    },
    {
      objectKey: "self",
      inputKey: "self",
      label: "Rule",
      type: "reference",
      ReactIcon: iconsByTypename.RuleDefinitionObject,
      asString: (data) => data?.identifier,
      getValue: (data) => {
        return {
          __typename: "RuleDefinitionObject",
          name: data?.name,
          id: data?.id,
          identifier: data?.identifier,
          entityId: data?.entity?.id,
        };
      },
      getValueOptions: (data) => ({
        isNullable: false,
        ...getReferenceValueOptionsByTypename("RuleDefinitionObject")([
          data?.entity?.identifier,
        ]),
        style: "self",
      }),
      fieldProps: {
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: true,
        isLockedLeft: true,
        isResizable: true,
      },
    },
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: BookTextIcon,
      asString: (data) => data?.name ?? "",
      getValue: (data) => data?.name ?? "",
      // Reaches the node store so the title bar can edit it in the create
      // form; ``NodeLayout`` leaves the title field out of the rows.
      createProps: {
        isRequired: true,
        isEditable: true,
      },
      updateProps: {
        isVisible: false,
      },
    },
    {
      // Edited in the Rule group; sent with the rule it creates.
      objectKey: "conditions",
      groupId: "rule",
      label: "Conditions",
      type: "custom",
      asString: () => "",
      getValue: () => () => null,
      getValueForApi: (data) => {
        const conditions = (data as unknown as RuleDraft)?.conditions;
        return conditions ? serializeFilterGroup(conditions) : [];
      },
      createProps: {
        isRequired: false,
        isEditable: true,
      },
      updateProps: {
        isVisible: false,
      },
    },
    {
      objectKey: "suggestions",
      groupId: "rule",
      label: "Actions",
      type: "custom",
      asString: () => "",
      getValue: () => () => null,
      getValueForApi: (data) =>
        ((data as unknown as RuleDraft)?.suggestions ?? []).map(
          (suggestion) => ({
            name: suggestion.name,
            type: suggestion.type,
            ...suggestion._input,
          }),
        ),
      createProps: {
        isRequired: false,
        isEditable: true,
      },
      updateProps: {
        isVisible: false,
      },
    },
    {
      objectKey: "suggestionType",
      label: "Type",
      type: "enum",
      ReactIcon: TagIcon,
      asString: (data) => {
        const type = primarySuggestionType(data);
        return type ? SUGGESTION_TYPE_LABELS[type] : "";
      },
      getValue: (data) => primarySuggestionType(data),
      getValueOptions: () => ({
        enum: RULE_SUGGESTION_TYPE_OPTIONS,
        getColors: (mode) => ({
          [RuleDefinitionSuggestionTypeEnum.MatchDocument]:
            EnumColors.BLUE[mode],
          [RuleDefinitionSuggestionTypeEnum.Categorize]: EnumColors.GREEN[mode],
          [RuleDefinitionSuggestionTypeEnum.Duplicate]: EnumColors.ORANGE[mode],
          [RuleDefinitionSuggestionTypeEnum.Split]: EnumColors.AQUA[mode],
          [RuleDefinitionSuggestionTypeEnum.Transfer]: EnumColors.GREY_1[mode],
          [RuleDefinitionSuggestionTypeEnum.Pair]: EnumColors.GREY_2[mode],
        }),
      }),
      updateProps: {
        isEditable: false,
        isVisible: (data) => primarySuggestionType(data) != null,
      },
      summaryProps: {
        isVisible: (data) => primarySuggestionType(data) != null,
      },
      fieldProps: {
        // Worked out here from the rule's suggestions: not written.
        exportPath: null,
        headerFieldType: "enum",
        isSortable: false,
        isFilterable: false,
      },
    },
    {
      objectKey: "applicableEntities",
      label: "Entities",
      type: "references",
      ReactIcon: BuildingIcon,
      asString: (data) =>
        ruleApplicableEntities(data)
          .map((entity) => entity.name?.trim() || entity.identifier)
          .join(", "),
      getValue: (data) =>
        ruleApplicableEntities(data).map((entity) => ({
          __typename: "EntityObject",
          id: entity.id,
          identifier: entity.identifier,
          name: entity.name?.trim() || entity.identifier,
        })),
      updateProps: {
        isEditable: false,
        isVisible: (data) => ruleApplicableEntities(data).length > 0,
      },
      summaryProps: {
        isVisible: (data) => ruleApplicableEntities(data).length > 0,
      },
      fieldProps: {
        // Worked out here from the rule and its delegations: not written.
        exportPath: null,
        headerFieldType: "custom",
        isSortable: false,
        isFilterable: false,
        isResizable: true,
      },
    },
    {
      objectKey: "description",
      groupId: "overview",
      label: "Description",
      type: "text",
      ReactIcon: FileTextIcon,
      asString: (data) => data?.description ?? "",
      getValue: (data) => data?.description ?? "",
      getValueOptions: () => ({
        placeholder: "This rule handles the process of...",
      }),
      createProps: {
        isRequired: false,
        isEditable: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        headerFieldType: "string",
        isSortable: true,
        isFilterable: true,
        width: Values.DEFAULT_COLUMN_WIDTH * 2,
        maxWidth: 800,
      },
    },
    {
      objectKey: "ruleStatus",
      inputKey: "status",
      groupId: "overview",
      label: "Enabled",
      type: "boolean",
      ReactIcon: ToggleRightIcon,
      asString: (data) =>
        isRuleEnabled(
          (data as { ruleStatus?: string | boolean | null })?.ruleStatus,
        )
          ? "Enabled"
          : "Disabled",
      getValue: (data) =>
        isRuleEnabled(
          (data as { ruleStatus?: string | boolean | null })?.ruleStatus,
        ),
      getValueForApi: (data) => {
        const raw = (data as { ruleStatus?: string | boolean | null })
          ?.ruleStatus;
        return isRuleEnabled(raw) ? "ACTIVE" : "INACTIVE";
      },
      createProps: {
        isRequired: false,
        isEditable: true,
      },
      updateProps: {
        isRequired: false,
        isEditable: true,
      },
      summaryProps: {
        isVisible: true,
      },
      fieldProps: {
        exportPath: "status",
        headerFieldType: "boolean",
        isSortable: true,
        isFilterable: true,
      },
    },
  ],
};

export { object as RuleDefinitionObject };
